import React, { useCallback, useMemo, useState } from 'react'
import { SemanticCOLORS } from 'semantic-ui-react/dist/commonjs/generic'
import { useSelector } from '../../../../hooks/useSelector'
import PromiseMenuItem from '../../../../components/PromiseMenuItem'
import usePlayerActions from '../../hooks/usePlayerActions'
import { doBlobbyStuff } from '../../../../utils/doBlobbyStuff'
import moment from 'moment'
import { GAME_VERSION } from '../../../../GAME_VERSION'
import { Cards, Modal, Typography, TypographyProps } from '@prometheus/components'
import PromiseCard from '@/components/PromiseCard'

interface GameMenuProps {
  onCancel: () => void
  open: boolean
}

interface GameMenuItems {
  title: string
  desc: string
  color: TypographyProps['color']
  action: () => void
  disabled?: boolean
}

export const GameMenu = ({ onCancel, open }: GameMenuProps) => {
  const owner = useSelector(s => s.game.game?.owner)
  const uid = useSelector(s => s.auth.uid) || ''
  const name = useSelector(s => s.game.game?.name)
  const disableTimer = useSelector(s => s.game.metadata?.disable_timer)
  const year = useSelector(s => s.game.metadata?.turn)
  const dead = useSelector(s => s.game.metadata?.players[uid].dead)
  const players_choosing_planet = useSelector(s => s.game.metadata?.players_choosing_planet)
  const [count, setCount] = useState(0)

  const {
    onLeaveGame,
    onEndGame,
    onExportGame,
    onPauseGame,
  } = usePlayerActions()

  useMemo(() => {
    if (open) {
      setCount(0)
    }
  }, [open])

  const onSafelyEndGame = useCallback(() => {
    if (count > 0) {
      onEndGame()
    } else {
      setCount(c => c + 1)
    }
  }, [onEndGame, count])

  const onDownloadGame = useCallback(async () => {
    onCancel()
    const result = await onExportGame()
    const blob = new Blob([JSON.stringify({
      ...result.data,
      version: GAME_VERSION,
    }, null, 2)], { type: 'application/json' })
    doBlobbyStuff(blob, `prometheus-${name}-${moment(new Date()).format('YYYY-MM-DD-HH-mm-ss') + 'Year ' + year}`)
  }, [onExportGame, onCancel, name, year])

  const items: GameMenuItems[] = useMemo(() => {
    const _items: GameMenuItems[] = [{
      title: 'Save Game',
      desc: 'Download the game to be able to resume it later. (This will not work well if a new version of the game is released)',
      color: 'tertiary',
      action: onDownloadGame,
    }, {
      title: 'Pause Game',
      desc: 'I have to go pee, but that turn timer is way too cruel!',
      color: 'secondary',
      disabled: dead,
      action: () => { onCancel(); onPauseGame(true) },
    }, {
      title: 'Abandon Colony',
      desc: 'Abandon my colony and return to the main menu.',
      color: 'warning',
      disabled: dead || (!!uid && (players_choosing_planet?.indexOf(uid) !== -1)),
      action: () => { onCancel(); onLeaveGame() },
    }, {
      title: count === 0 ? 'Finish Game' : 'Are you sure?',
      desc: count === 0 ? 'The game is over, the planets have been conquered, our income is too high, and we want the option to start a new game.' : 'Press again to confirm I want the game to end.',
      color: 'danger',
      disabled: owner !== uid,
      action: onSafelyEndGame,
    }]

    if (disableTimer) {
      _items.splice(1, 1)
    }

    return _items
  }, [uid, owner, disableTimer, count, dead, onCancel, onDownloadGame, onLeaveGame, onPauseGame, onSafelyEndGame, players_choosing_planet])

  if (!open) return null;

  return (
    <Modal onClose={onCancel}>
      <Cards style={{ width: 'min(300px, calc(100vw - 32px))' }}>
        {items.map(({ action, title, desc, disabled, color }) => (
          <PromiseCard interactive padding="sm" key={title} onClick={action} disabled={disabled} ignoreGameLogic>
            <Typography variant="h4" retro style={{ lineHeight: 1 }} color={color}>{title}</Typography>
            <br />
            <Typography variant="body2">{desc}</Typography>
          </PromiseCard>
        ))}
      </Cards>
    </Modal>
  )
}
